import React from "react";
import { ArrowRight } from "lucide-react";
import { Review } from "@/services/bedrock";
import { CoachReviewCard } from "../components/CoachReviewCard";
import { cn } from "@/lib/utils";

function ReviewColumn({
  title,
  reviews,
  onViewAll,
  onReviewClick,
}: {
  title: string;
  reviews: Review[];
  onViewAll: () => void;
  onReviewClick: (review: Review) => void;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-4 flex items-end justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight mb-0">{title}</h2>
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          View all
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex flex-col">
        {reviews.map(review => (
          <CoachReviewCard key={review.id} review={review} grouped onClick={() => onReviewClick(review)} />
        ))}
      </div>
    </div>
  );
}

export function EvidenceReviews({
  topReviews,
  bottomReviews,
  disabled = false,
  onViewAllTop,
  onViewAllBottom,
  onReviewClick,
}: {
  topReviews?: Review[];
  bottomReviews?: Review[];
  disabled?: boolean;
  onViewAllTop: () => void;
  onViewAllBottom: () => void;
  onReviewClick: (review: Review) => void;
}) {
  const top = topReviews?.length ? topReviews : null;
  const bottom = bottomReviews?.length ? bottomReviews : null;
  if (!top && !bottom) return null;

  return (
    <section className="font-sans" aria-busy={disabled}>
      <div
        className={cn(
          "grid gap-8 transition-opacity",
          top && bottom && "lg:grid-cols-2",
          disabled && "pointer-events-none opacity-50"
        )}
      >
        {top && (
          <ReviewColumn title="Top reviews" reviews={top} onViewAll={onViewAllTop} onReviewClick={onReviewClick} />
        )}
        {bottom && (
          <ReviewColumn
            title="Bottom reviews"
            reviews={bottom}
            onViewAll={onViewAllBottom}
            onReviewClick={onReviewClick}
          />
        )}
      </div>
    </section>
  );
}
